/**
 * @fileoverview Address Validation Hook using Smarty US Street Address API
 * 
 * Provides USPS-certified address validation with standardized formatting
 * and deliverability status. Used across all address capture forms.
 */

import { useState, useCallback } from 'react';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { useToast } from '@/hooks/use-toast';

export interface AddressInput {
  street: string;
  city: string;
  state: string;
  zipcode: string;
}

export interface StandardizedAddress {
  street: string;
  street2?: string;
  city: string;
  state: string;
  zipcode: string;
  plus4?: string;
  county?: string;
}

export type Deliverability = 'deliverable' | 'partially_deliverable' | 'undeliverable' | 'unknown';

export interface ValidationResult {
  isValid: boolean;
  deliverability: Deliverability;
  standardizedAddress?: StandardizedAddress;
  dpvMatchCode?: string;
  residentialIndicator?: 'residential' | 'commercial' | 'unknown';
  suggestions?: StandardizedAddress[];
  error?: string;
}

/**
 * Hook for validating addresses using Smarty US Street Address API
 * 
 * @returns Object with validation function and state
 * 
 * @example
 * ```tsx
 * function AddressForm() {
 *   const { validateAddress, isValidating, lastResult } = useAddressValidation();
 * 
 *   const handleValidate = async () => {
 *     const result = await validateAddress({
 *       street: '123 Main St',
 *       city: 'New York',
 *       state: 'NY',
 *       zipcode: '10001'
 *     });
 *     
 *     if (result.isValid) {
 *       // Use result.standardizedAddress for corrected format
 *     }
 *   };
 * }
 * ```
 */
export function useAddressValidation() {
  const [isValidating, setIsValidating] = useState(false);
  const [lastResult, setLastResult] = useState<ValidationResult | null>(null);
  const { toast } = useToast();

  const validateAddress = useCallback(async (address: AddressInput): Promise<ValidationResult> => {
    setIsValidating(true);
    
    try {
      const { data, error } = await invokeEdgeFunction('validate-address', {
        body: address,
        callerOperation: 'useAddressValidation.validate',
      });

      if (error) {
        console.error('Address validation error:', error);
        const result: ValidationResult = {
          isValid: false,
          deliverability: 'unknown',
          error: 'Failed to validate address. Please try again.',
        };
        setLastResult(result);
        return result;
      }

      const result = data as ValidationResult;
      setLastResult(result);
      
      // Show toast based on result
      if (result.isValid) {
        if (result.deliverability === 'deliverable') {
          toast({
            title: 'Address Verified',
            description: 'This address has been verified as deliverable.',
          });
        } else if (result.deliverability === 'partially_deliverable') {
          toast({
            title: 'Address Partially Verified',
            description: 'This address exists but may have delivery issues.',
            variant: 'default',
          });
        }
      } else {
        toast({
          title: 'Address Not Found',
          description: result.error || 'Unable to verify this address. Please check and try again.',
          variant: 'destructive',
        });
      }

      return result;
    } catch (err) {
      console.error('Address validation error:', err);
      const result: ValidationResult = {
        isValid: false,
        deliverability: 'unknown',
        error: 'An unexpected error occurred during validation.',
      };
      setLastResult(result);
      
      toast({
        title: 'Validation Error',
        description: 'Unable to validate address at this time.',
        variant: 'destructive',
      });
      
      return result;
    } finally {
      setIsValidating(false);
    }
  }, [toast]);

  const clearValidation = useCallback(() => {
    setLastResult(null);
  }, []);

  return {
    validateAddress,
    isValidating,
    lastResult,
    clearValidation,
  };
}
